import { jwtDecode } from 'jwt-decode';

interface StoredTokens {
  token: string;
  refreshToken: string;
  expiresAt: number;
}

interface TokenPayload {
  exp: number;
  [key: string]: any;
}

const TOKEN_STORAGE_KEY = 'breedlogic_auth_tokens';
const TOKEN_VERSION = '1'; // For migration handling

export const tokenStorage = {
  /**
   * Save tokens to secure storage
   */
  saveTokens(token: string, refreshToken: string): void {
    try {
      // Decode token to get expiry
      const decoded = jwtDecode<TokenPayload>(token);
      const tokens: StoredTokens = {
        token,
        refreshToken,
        expiresAt: decoded.exp * 1000, // Convert to milliseconds
      };

      // For production, implement secure cookie storage instead
      if (process.env.NODE_ENV === 'production') {
        // TODO: Implement secure cookie storage
        console.warn(
          'Using localStorage in production - implement secure cookies',
        );
      }

      localStorage.setItem(
        TOKEN_STORAGE_KEY,
        JSON.stringify({
          version: TOKEN_VERSION,
          ...tokens,
        }),
      );

      console.log('[TokenStorage] Tokens saved successfully');
    } catch (error) {
      console.error('[TokenStorage] Failed to save tokens:', error);
    }
  },

  /**
   * Retrieve stored tokens if valid
   */
  getTokens(): StoredTokens | null {
    try {
      const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!stored) {
        console.log('[TokenStorage] No stored tokens found');
        return null;
      }

      const data = JSON.parse(stored);

      // Check version for migration handling
      if (data.version !== TOKEN_VERSION) {
        console.log('[TokenStorage] Token version mismatch, clearing tokens');
        this.clearTokens();
        return null;
      }

      const tokens: StoredTokens = {
        token: data.token,
        refreshToken: data.refreshToken,
        expiresAt: data.expiresAt,
      };

      // Check if tokens are expired
      if (Date.now() >= tokens.expiresAt) {
        console.log('[TokenStorage] Tokens expired, clearing storage');
        this.clearTokens();
        return null;
      }

      // Use debug level for successful retrieval to reduce spam
      if (process.env.NODE_ENV === 'development') {
        console.debug('[TokenStorage] Retrieved valid tokens');
      }
      return tokens;
    } catch (error) {
      console.error('[TokenStorage] Failed to retrieve tokens:', error);
      this.clearTokens();
      return null;
    }
  },

  /**
   * Clear stored tokens
   */
  clearTokens(): void {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('msal'))
      .forEach((key) => localStorage.removeItem(key));

    localStorage.removeItem(TOKEN_STORAGE_KEY);
    // Also clear any auth-related session data
    sessionStorage.removeItem('auth_session');
    console.log('[TokenStorage] Tokens cleared');
  },

  /**
   * Check if we have valid stored tokens
   */
  hasValidTokens(): boolean {
    const tokens = this.getTokens();
    if (!tokens) return false;

    // Check if token expires in more than 5 minutes
    const timeUntilExpiry = (tokens.expiresAt - Date.now()) / 1000;
    return timeUntilExpiry > 300;
  },

  /**
   * Get time until token expiry in seconds
   */
  getTimeUntilExpiry(): number {
    const tokens = this.getTokens();
    if (!tokens) return 0;

    return Math.max(0, (tokens.expiresAt - Date.now()) / 1000);
  },
};

// Add debug utilities to window in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).tokenStorage = tokenStorage;
}
